import { defineStore } from 'pinia';
import { supabase } from '@/lib/supabaseClient';
import router from '@/router';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    loading: false,
    error: null,
  }),
  getters: {
    /** Supabase user ID (UUID string) or null */
    userId: (state) => state.user?.id ?? null,
    isAuthenticated: (state) => !!state.user,
  },
  actions: {
    async register(email, password) {
      this.loading = true;
      this.error = null;
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        this.user = data.user;
        console.log("User registered successfully!");
        router.push('/');
      } catch (error) {
        console.error(error.message);
        alert(error.message);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    async googleLogin() {
      this.loading = true;
      this.error = null;
      try {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        // OAuth redirects the browser; user state is set by onAuthStateChange
        // after redirect back.
        console.log("Redirecting to Google OAuth...");
      } catch (error) {
        console.error(error.message);
        alert(error.message);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    async login(email, password) {
      this.loading = true;
      this.error = null;
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        this.user = data.user;
        console.log("User logged in successfully!");
        router.push('/');
      } catch (error) {
        console.error(error.message);
        alert(error.message);
        this.error = "Invalid email or password.";
      } finally {
        this.loading = false;
      }
    },
    async logout() {
      this.loading = true;
      this.error = null;
      try {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        this.user = null;
        console.log("User logged out successfully!");
        router.push('/main');
      } catch (error) {
        console.error(error.message);
        alert(error.message);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    init() {
      // Listen for auth state changes (login, logout, token refresh, OAuth redirect)
      supabase.auth.onAuthStateChange((_event, session) => {
        this.user = session?.user ?? null;
      });
      // Also set the initial user from the current session
      supabase.auth.getSession().then(({ data: { session } }) => {
        this.user = session?.user ?? null;
      });
    },
  },
});
